import { rendererMap } from "@/components/CSV/CellRenderers";
import type { ColumnSpec } from "@/constants/types/csv";
import {
	HotColumn,
	type HotTableRef,
	HotTable as RawHotTable,
} from "@handsontable/react-wrapper";
import { Box } from "@mui/material";
import type Handsontable from "handsontable";
import { useRef } from "react";

interface ReusableHotTableProps<RowT extends Handsontable.RowObject> {
	data: RowT[];
	columns: ColumnSpec[];
	applyRowRules?: (
		instance: Handsontable,
		rowIndex: number,
		rowData: RowT,
	) => void;
	hotProps?: Omit<
		React.ComponentProps<typeof RawHotTable>,
		"data" | "colHeaders" | "rowHeaders"
	>;
}

export function ReusableHotTable<RowT extends Handsontable.RowObject>({
	data,
	columns,
	hotProps,
}: ReusableHotTableProps<RowT>) {
	const hotRef = useRef<HotTableRef>(null);

	return (
		<Box sx={{ borderRadius: 5 }}>
			<RawHotTable
				ref={hotRef}
				data={data}
				colHeaders={columns.map((c) => c.header)}
				rowHeaders
				width="100%"
				height="calc(100vh - 260px)"
				className="custom-handsontable"
				licenseKey={
					process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY ||
					"non-commercial-and-evaluation"
				}
				{...hotProps}
			>
				{columns.map((col) => (
					<HotColumn
						key={col.key as string}
						data={col.key as string}
						width={col.width}
						type={col.type}
						source={col.source}
						renderer={(hp) => {
							const Renderer = rendererMap[col.type];
							return <Renderer {...hp} validator={col.validator} data={data} />;
						}}
					/>
				))}
			</RawHotTable>
		</Box>
	);
}
